import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Badge, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faBox, faExternalLinkAlt, faExclamationTriangle, faTag } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import api from '../config/api';
import './OrderSearch.css';

const OrderSearch = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [showNoOrderModal, setShowNoOrderModal] = useState(false);
  const [searchType, setSearchType] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [hasScrolled, setHasScrolled] = useState(false);
  const lastOrderRef = useRef(null);

  const scrollToLastOrder = () => {
    if (lastOrderRef.current) {
      lastOrderRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  useEffect(() => {
    if (searchResults && searchResults.length > 0 && !hasScrolled) {
      scrollToLastOrder();
    }
    
    // Temizleme fonksiyonu
    return () => {
      setHasScrolled(false);
    };
  }, [searchResults, hasScrolled]);

  const handlePhoneSearch = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      toast.error('Lütfen telefon numaranızı girin');
      return;
    }

    const phoneRegex = /^5[0-9]{9}$/;
    const cleanPhone = phoneNumber.replace(/\D/g, '');

    if (!phoneRegex.test(cleanPhone)) {
      toast.error('Geçerli bir telefon numarası girin (5xx xxx xx xx)');
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/orders/search/phone/${cleanPhone}`);
      
      // Backend'den gelen yanıtı doğrudan kullan (artık backend maskeliyor)
      setSearchResults(response.data);
      if (response.data.length === 0) {
        setSearchType('telefon numarası');
        setSearchValue(cleanPhone);
        setShowNoOrderModal(true);
      } else {
        setTimeout(scrollToLastOrder, 100);
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Sipariş arama sırasında bir hata oluştu';
      toast.error(message);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleOrderSearch = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      toast.error('Lütfen sipariş numarasını girin');
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/orders/search/number/${orderNumber}`);
      
      // Backend'den gelen yanıtı doğrudan kullan (artık backend maskeliyor)
      setSearchResults(response.data);
      if (response.data.length === 0) {
        setSearchType('sipariş numarası');
        setSearchValue(orderNumber);
        setShowNoOrderModal(true);
      } else {
        setTimeout(scrollToLastOrder, 100);
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Sipariş arama sırasında bir hata oluştu';
      toast.error(message);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhoneNumber(value);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'Beklemede', variant: 'warning' },
      'processing': { text: 'İşleniyor', variant: 'info' },
      'completed': { text: 'Tamamlandı', variant: 'success' },
      'failed': { text: 'Başarısız', variant: 'danger' }
    };

    const statusInfo = statusMap[status] || { text: status, variant: 'secondary' };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  return (
    <Container className="py-5">
      <h2 className="text-center mb-4">Sipariş Arama</h2>
      <Row className="justify-content-center">
        <Col md={6}>
          <Card className="search-card mb-4">
            <Card.Body>
              <h5 className="mb-3">Telefon Numarası ile Ara</h5>
              <Form onSubmit={handlePhoneSearch}>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="tel"
                    placeholder="Telefon numarası (5xx xxx xx xx)"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    pattern="^5[0-9]{9}$"
                    maxLength="10"
                  />
                </Form.Group>
                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100 order-search-button"
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      Ara
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="search-card">
            <Card.Body>
              <h5 className="mb-3">Sipariş Numarası ile Ara</h5>
              <Form onSubmit={handleOrderSearch}>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Sipariş numarası"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                  />
                </Form.Group>
                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100 order-search-button"
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      Ara
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {searchResults && searchResults.length > 0 && (
        <Row className="mt-4">
          <Col>
            <div className="order-results-container">
              {[...searchResults].reverse().map((order, index) => (
                <Card 
                  key={order.id} 
                  className="order-card mb-3"
                  ref={index === 0 ? lastOrderRef : null}
                >
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="order-info-section h6">Sipariş No</h6>
                      <h6 className="mb-0">{order.order_number}</h6>
                      <small className="text-muted">{formatDate(order.created_at)}</small>
                    </div>
                    {getStatusBadge(order.status)}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <div className="order-info-section">
                          <h6>Sipariş Bilgileri</h6>
                          <p className="mb-1">
                            <strong>Toplam Tutar:</strong> {formatPrice(order.total_amount)}
                          </p>
                          <p className="mb-1">
                            <strong>Alıcı:</strong> {order.first_name} {order.last_name}
                          </p>
                          <p className="mb-0">
                            <strong>Telefon:</strong> {order.phone}
                          </p>
                          
                          {/* İndirim bilgisi */}
                          {order.discount && (
                            <p className="mb-0 mt-2 discount-info">
                              <FontAwesomeIcon icon={faTag} className="me-1 text-success" />
                              <strong>İndirim:</strong> {order.discount.code} 
                              {order.discount.percentage && (
                                <span> ({order.discount.percentage}%)</span>
                              )}
                              <span className="ms-1 text-success">
                                -{formatPrice(order.discount.amount)}
                              </span>
                            </p>
                          )}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="order-info-section">
                          <h6>Teslimat Adresi</h6>
                          <p className="mb-0">
                            {order.address}, {order.district}, {order.city}
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        {order.shipments && order.shipments[0] && (
                          <div className="order-info-section">
                            <h6>Kargo Bilgileri</h6>
                            <p className="mb-1">
                              <strong>Kargo Firması:</strong> {order.shipments[0].provider_code === 'KOLAYGELSIN' ? 'Kolay Gelsin' : order.shipments[0].provider_code}
                            </p>
                            <p className="mb-0">
                              <strong>Takip No:</strong>{' '}
                              <a href={`https://app.geliver.io/tracking/${order.shipments[0].geliver_shipment_id}`} target="_blank" rel="noopener noreferrer">
                                {order.shipments[0].tracking_number}{' '}
                                <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                              </a>
                            </p>
                          </div>
                        )}
                      </Col>
                    </Row>
                    {order.cart_items && (
                      <div className="mt-3">
                        <h6>Sipariş Detayı</h6>
                        <div className="order-items">
                          {order.cart_items.map((item, index) => (
                            <div key={index} className="order-item">
                              <div className="order-item-image">
                                <img 
                                  src={item.image_url || '/images/no-image.webp'} 
                                  alt={item.name}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/images/no-image.webp';
                                  }}
                                />
                              </div>
                              <div className="order-item-details">
                                <h6>{item.name}</h6>
                                
                                {/* Telefon modeli bilgisi */}
                                {item.phone_model && (
                                  <p className="mb-0">
                                    {item.phone_model.brand && item.phone_model.model_name 
                                      ? `${item.phone_model.brand} ${item.phone_model.model_name}` 
                                      : item.phone_model.model_name || item.phone_model}
                                  </p>
                                )}
                                
                                {/* Renk bilgisi */}
                                {(item.color || item.color_id) && (
                                  <div className="color-info mb-0">
                                    <span>Renk: </span>
                                    {item.color ? item.color.name : 
                                     item.color_id === 1 ? "Siyah" : 
                                     item.color_id === 2 ? "Beyaz" : 
                                     item.color_id === 3 ? "Saydam" : 
                                     `Renk ${item.color_id}`}
                                  </div>
                                )}
                                
                                <p className="mb-0">Adet: {item.quantity}</p>
                                <p className="mb-0">Fiyat: {formatPrice(item.price)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Col>
        </Row>
      )}

      {/* No Order Modal */}
      <Modal 
        show={showNoOrderModal} 
        onHide={() => setShowNoOrderModal(false)}
        centered
        className="no-order-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Sipariş Bulunamadı
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Girdiğiniz {searchType} <strong>"{searchValue}"</strong> ile ilişkili sipariş bulunamadı.
          </p>
          <p className="mt-3 mb-0">
            Lütfen girdiğiniz bilgileri kontrol edip tekrar deneyiniz.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="primary" 
            onClick={() => setShowNoOrderModal(false)}
            className="order-search-button"
          >
            Tamam
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrderSearch; 